import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  background-color: #000;
  z-index: 1000;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  height: auto;
`;

const LogoBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Logo = styled(Link)`
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fff;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const EmailLink = styled.a`
  font-size: 0.9rem;
  font-weight: 400;
  color: #aaa;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #fff;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const PrimaryNav = styled.nav`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    gap: 20px;
    width: 100%;
    margin-top: 5px;
  }

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const SecondaryNavContainer = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 0;
  width: 100%;
`;

const SecondaryNav = styled.nav`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 15px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const NavLink = styled(Link)`
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: color 0.2s ease-in-out;
  display: flex;
  align-items: center;

  &:hover {
    color: #888;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
`;

const IconNavLink = styled(NavLink)`
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
  }
`;

const HomeIconLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover svg {
    stroke: #888;
  }
`;

const HamburgerMenuContainer = styled.div`
  position: relative;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease-in-out;
  line-height: 1;

  &:hover {
    color: #888;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HamburgerDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  min-width: 150px;
  z-index: 1001;
`;

const DropdownLink = styled(Link)`
  display: block;
  padding: 12px 20px;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

function Header() {
  const [hamburgerOpen, setHamburgerOpen] = useState(false);

  return (
    <HeaderContainer onClick={() => setHamburgerOpen(false)}>
      <LogoBar>
        <LogoContainer>
          <Logo to="/">JOEY M ROBINSON STUDIO</Logo>
          <EmailLink href="mailto:joeymrobinson3@gmail.com">joeymrobinson3@gmail.com</EmailLink>
        </LogoContainer>
        <PrimaryNav>
          <HomeIconLink to="/" title="Home">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </HomeIconLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/contact">Contact</NavLink>
          <NavLink to="/guest-artists">Guest Artists</NavLink>
          <HamburgerMenuContainer onClick={(e) => e.stopPropagation()}>
            <HamburgerButton
              onClick={() => setHamburgerOpen(!hamburgerOpen)}
              aria-label="Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </HamburgerButton>
            {hamburgerOpen && (
              <HamburgerDropdown>
                <DropdownLink to="/admin" onClick={() => setHamburgerOpen(false)}>Admin</DropdownLink>
              </HamburgerDropdown>
            )}
          </HamburgerMenuContainer>
        </PrimaryNav>
      </LogoBar>

      <SecondaryNavContainer>
        <SecondaryNav>
          <NavLink to="/available">On Sale</NavLink>
          <NavLink to="/commission">Commissions</NavLink>
          <IconNavLink to="/search">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            Search
          </IconNavLink>
          <IconNavLink to="/favorites">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            Favs
          </IconNavLink>
        </SecondaryNav>
      </SecondaryNavContainer>
    </HeaderContainer>
  );
}

export default Header;